import { useState, useEffect } from "react";
import { DocumentNode, useQuery } from "@apollo/client";

import { ICommentsConnection, IClipComment } from "./@types";

interface ICommentsViewModelProps {
  query: DocumentNode;
  variables: Record<string, unknown>;
  // Must be a stable, module-scope function — it participates in effect deps.
  getConnection: (data: any) => ICommentsConnection | null | undefined;
  pollInterval?: number;
}

const PAGE_SIZE = 25;

const CommentsViewModel = ({
  query,
  variables,
  getConnection,
  pollInterval,
}: ICommentsViewModelProps) => {
  const [connection, setConnection] = useState<ICommentsConnection | null>(
    null,
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const {
    error,
    loading,
    fetchMore,
    data: queryData,
  } = useQuery(query, {
    variables: {
      ...variables,
      first: PAGE_SIZE,
      after: null,
      last: null,
      before: null,
    },
    fetchPolicy: "cache-and-network",
    pollInterval,
  });

  useEffect(() => {
    const conn = getConnection(queryData);
    if (!conn?.nodes) return;

    const incoming = [...conn.nodes].reverse();

    setConnection((prev) => {
      if (!pollInterval || !prev) {
        return { ...conn, nodes: incoming };
      }

      // Poll ticks return only the newest page. Inside the window that page
      // covers the server is authoritative, so comments it no longer returns
      // (deleted) drop out instead of lingering forever; anything older that
      // the page no longer reaches is kept from the paginated history.
      const incomingIds = new Set(
        incoming.map((node: IClipComment) => node.id),
      );
      const olderHistory = () => {
        if (incoming.length === 0) return [];
        // The page reaching the end of the connection means nothing older
        // exists server-side, so no local node survives outside it. Page
        // *length* cannot stand in for this: a local eviction shrinks the
        // cached page below PAGE_SIZE without ending the connection.
        if (conn.pageInfo?.hasNextPage === false) return [];

        const windowStart = prev.nodes.findIndex((node) =>
          incomingIds.has(node.id),
        );
        // No overlap: the window moved past everything we hold, so keep it all.
        return windowStart === -1
          ? prev.nodes
          : prev.nodes.slice(0, windowStart);
      };

      return {
        ...conn,
        nodes: [...olderHistory(), ...incoming],
        pageInfo: {
          ...conn.pageInfo,
          endCursor: prev.pageInfo.endCursor,
          hasNextPage: prev.pageInfo.hasNextPage,
        },
      };
    });
  }, [queryData, getConnection, pollInterval]);

  const loadMoreComments = async () => {
    const endCursor = connection?.pageInfo.endCursor;
    if (!connection?.pageInfo.hasNextPage || isLoadingMore || !endCursor) {
      return;
    }
    setIsLoadingMore(true);

    try {
      const result = await fetchMore({
        variables: { ...variables, first: PAGE_SIZE, after: endCursor },
      });
      const conn = getConnection(result.data);
      if (!conn) return;

      const older = [...conn.nodes].reverse();
      setConnection((prev) => {
        if (!prev) return { ...conn, nodes: older };
        if (older.length === 0) {
          return {
            ...prev,
            pageInfo: { ...prev.pageInfo, hasNextPage: false },
          };
        }
        return {
          ...prev,
          nodes: [...older, ...prev.nodes],
          pageInfo: conn.pageInfo,
        };
      });
    } catch {
      // Swallowed like the previous implementation; surfacing load-more
      // failures is a future UX decision.
    } finally {
      setIsLoadingMore(false);
    }
  };

  return {
    connection,
    rawData: queryData,
    error,
    loading,
    loadMoreComments,
    hasNextPage: connection?.pageInfo.hasNextPage ?? false,
    isLoadingMore,
  };
};

export { CommentsViewModel, PAGE_SIZE };
